import React from 'react'
import clsx from 'clsx'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import { makeStyles } from '@material-ui/core/styles'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import DeleteOutlineIcon from 'components/material-symbol/icons/DeleteOutline'
import FlagIcon from 'components/material-symbol/icons/Flag'
import NotificationsOffOutlinedIcon from 'components/material-symbol/icons/NotificationsOff'
import NotificationsActiveOutlinedIcon from 'components/material-symbol/icons/NotificationsActive'
import MarkunreadMailboxIcon from 'components/material-symbol/icons/MarkunreadMailbox'
import PushPinIcon from 'components/material-symbol/icons/PushPin'
import ReplyIcon from 'components/material-symbol/icons/Reply'
import {
  useMessageComposer,
  useMessageContext,
  useTranslationContext,
} from 'stream-chat-react'

const MESSAGE_ACTIONS = {
  quote: 'quote',
  pin: 'pin',
  markUnread: 'markUnread',
  flag: 'flag',
  mute: 'mute',
  edit: 'edit',
  delete: 'delete',
  deleteForMe: 'deleteForMe',
}

const useMenuItemStyles = makeStyles(() => ({
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
}))

const ActionMenuItem = ({
  icon: Icon,
  label,
  menuIconClassName,
  onClick,
  destructive = false,
}) => (
  <MenuItem
    className={clsx('messaging-message-action-menu-item', {
      'messaging-message-action-menu-item--destructive': destructive,
    })}
    onClick={onClick}
  >
    <ListItemIcon className={menuIconClassName}>
      <Icon fontSize="small" />
    </ListItemIcon>
    <ListItemText primary={label} />
  </MenuItem>
)

const MessagingMessageActionsBox = props => {
  const menuItemClasses = useMenuItemStyles()
  const {
    className,
    getMessageActions,
    handleDelete,
    handleEdit,
    handleFlag,
    handleMarkUnread,
    handleMute,
    handlePin,
    isUserMuted,
    onClose,
    open,
    ...restDivProps
  } = props

  const { customMessageActions, message, threadList } = useMessageContext(
    'MessagingMessageActionsBox',
  )
  const messageComposer = useMessageComposer()
  const { t } = useTranslationContext('MessagingMessageActionsBox')
  const messageActions = getMessageActions()

  const handleQuote = () => {
    messageComposer.setQuotedMessage(message)
    const elements = message.parent_id
      ? document.querySelectorAll(
        '.str-chat__thread .str-chat__textarea__textarea',
      )
      : document.getElementsByClassName('str-chat__textarea__textarea')
    const textarea = elements.item(0)
    if (textarea instanceof HTMLTextAreaElement) {
      textarea.focus()
    }
  }

  const rootClassName = clsx(
    'str-chat__message-actions-box',
    'messaging-message-actions-box',
    className,
    {
      'str-chat__message-actions-box--open': open,
    },
  )

  const customActions = customMessageActions
    ? Object.keys(customMessageActions)
    : []

  const runAction = handler => event => {
    handler?.(event)
    onClose?.()
  }

  return (
    <div
      {...restDivProps}
      className={rootClassName}
      data-testid="message-actions-box"
    >
      <MenuList
        autoFocusItem
        aria-label={t('aria/Message Options')}
        className="str-chat__message-actions-list messaging-message-actions-list"
        role="menu"
      >
        {customActions.map(actionName => {
          const customHandler = customMessageActions[actionName]
          return (
            <ActionMenuItem
              key={actionName}
              icon={ReplyIcon}
              label={actionName}
              menuIconClassName={menuItemClasses.menuIcon}
              onClick={runAction(event => customHandler(message, event))}
            />
          )
        })}
        {messageActions.indexOf(MESSAGE_ACTIONS.quote) > -1 && (
          <ActionMenuItem
            icon={ReplyIcon}
            label={t('Reply')}
            menuIconClassName={menuItemClasses.menuIcon}
            onClick={runAction(handleQuote)}
          />
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.pin) > -1 &&
          !message.parent_id && (
            <ActionMenuItem
              icon={PushPinIcon}
              label={!message.pinned ? t('Pin') : t('Unpin')}
              menuIconClassName={menuItemClasses.menuIcon}
              onClick={runAction(handlePin)}
            />
          )}
        {messageActions.indexOf(MESSAGE_ACTIONS.markUnread) > -1 &&
          !threadList &&
          !!message.id && (
            <ActionMenuItem
              icon={MarkunreadMailboxIcon}
              label={t('Mark as unread')}
              menuIconClassName={menuItemClasses.menuIcon}
              onClick={runAction(handleMarkUnread)}
            />
          )}
        {messageActions.indexOf(MESSAGE_ACTIONS.flag) > -1 && (
          <ActionMenuItem
            icon={FlagIcon}
            label={t('Flag')}
            menuIconClassName={menuItemClasses.menuIcon}
            onClick={runAction(handleFlag)}
          />
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.mute) > -1 && (
          <ActionMenuItem
            icon={
              isUserMuted()
                ? NotificationsActiveOutlinedIcon
                : NotificationsOffOutlinedIcon
            }
            label={isUserMuted() ? t('Unmute') : t('Mute')}
            menuIconClassName={menuItemClasses.menuIcon}
            onClick={runAction(handleMute)}
          />
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.edit) > -1 && (
          <ActionMenuItem
            icon={EditOutlinedIcon}
            label={t('Edit Message')}
            menuIconClassName={menuItemClasses.menuIcon}
            onClick={runAction(handleEdit)}
          />
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.delete) > -1 && (
          <ActionMenuItem
            destructive
            icon={DeleteOutlineIcon}
            label={t('Delete')}
            menuIconClassName={menuItemClasses.menuIcon}
            onClick={runAction(handleDelete)}
          />
        )}
        {messageActions.indexOf(MESSAGE_ACTIONS.deleteForMe) > -1 &&
          !message.deleted_for_me && (
            <ActionMenuItem
              destructive
              icon={DeleteOutlineIcon}
              label={t('Delete for me')}
              menuIconClassName={menuItemClasses.menuIcon}
              onClick={runAction(event =>
                handleDelete(event, { deleteForMe: true }),
              )}
            />
          )}
      </MenuList>
    </div>
  )
}

export default MessagingMessageActionsBox
